import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { rates as exchangeRates } from '../../data/exchangeRates';
import './Account.css'; 
const API_URL = process.env.REACT_APP_API_URL;
const CustomerOrdersPage = ({ selectedCurrency }) => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  
  const [orders, setOrders] = useState([]);
  // Müşterinin GÜNCEL zaman dilimini tutan state
  const [customerTimezone, setCustomerTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('total');

  useEffect(() => {
    const fetchPageData = async () => {
      if (!token) { setLoading(false); return; }
      try {
        setLoading(true); setError('');
        
        const [ordersResponse, profileResponse] = await Promise.all([
            fetch(`${API_URL}/api/jobs/my-orders`, { headers: { 'Authorization': `Bearer ${token}` } }),
            fetch(`${API_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);

        if (!ordersResponse.ok) {
          const errorData = await ordersResponse.json();
          throw new Error(t(errorData.messageKey) || t('error_fetch_orders'));
        }
        
        const ordersData = await ordersResponse.json();
        setOrders(ordersData);
        
        if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            if (profileData.timezone) {
                // Gelen güncel profil bilgisini state'e ata
                setCustomerTimezone(profileData.timezone);
            }
        } else {
            console.warn("Could not fetch customer profile for timezone.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPageData();
  }, [token, t]);
  
  // formatDate artık her zaman müşterinin GÜNCEL zaman dilimini kullanıyor.
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Parametre olarak gelen 'customerTimezone' state'ini kullan
    return formatInTimeZone(new Date(dateString), customerTimezone, 'dd.MM.yyyy HH:mm');
  };
  const mapStatusForCustomer = (status) => {
    switch (status) {
      case 'pending_approval': case 'available': case 'assigned': case 'in_progress': case 'in_review': return 'in_progress';
      case 'completed': return 'completed';
      case 'cancelled': return 'cancelled';
      default: return 'unknown';
    }
  };

  const pendingOrders = useMemo(() => orders.filter(o => mapStatusForCustomer(o.status) === 'in_progress'), [orders]);
  const completedOrders = useMemo(() => orders.filter(o => mapStatusForCustomer(o.status) === 'completed'), [orders]);

  const filteredOrders = useMemo(() => {
    if (activeTab === 'pending') return pendingOrders;
    if (activeTab === 'completed') return completedOrders;
    return orders;
  }, [orders, activeTab, pendingOrders, completedOrders]);
  
  const renderContent = () => {
    if (loading) { return <tr><td colSpan="6" className="table-row-message">{t('loading_orders')}</td></tr>; }
    if (error) { return <tr><td colSpan="6" className="table-row-message error">{error}</td></tr>; }
    if (orders.length === 0) {
      return ( <tr><td colSpan="6" className="table-row-message empty-orders"><p>{t('no_orders_found')}</p><Link to="/order" className="cta-button">{t('create_first_order')}</Link></td></tr> );
    }
    if (filteredOrders.length === 0) {
        return <tr><td colSpan="6" className="table-row-message">{t('no_orders_in_category')}</td></tr>;
    }
    return filteredOrders.map(order => {
       let targetLangsArray = [];
        try {
            // Eğer order.target_langs geçerli bir JSON string'i ise,
            // (örneğin '["en", "de"]'), onu gerçek bir diziye dönüştür.
            targetLangsArray = JSON.parse(order.target_langs);
        } catch (e) {
            // Eğer bir hata olursa (örn: string boş veya bozuksa),
            // programın çökmesini engelle ve boş bir dizi kullan.
            console.error("target_langs parse edilemedi:", order.target_langs);
        }
        const customerStatus = mapStatusForCustomer(order.status);
        
        const priceInTry = parseFloat(order.price) || 0;
        const targetRate = exchangeRates[selectedCurrency] || 1;
        const convertedPrice = priceInTry * targetRate;

        return (
            <tr className="table-row" key={order.id}>
                <td>#{order.id}</td>
                <td>{order.source_lang.toUpperCase()} &rarr; {targetLangsArray.join(', ').toUpperCase()}</td>
                <td><span className={`status-badge status-${customerStatus}`}>{t(`customer_status_${customerStatus}`)}</span></td>
                <td className="price-cell">
                  {convertedPrice.toLocaleString(i18n.language, {
                      style: 'currency', 
                      currency: selectedCurrency || 'TRY'
                  })}
                </td>
                {/* Bu satır artık her zaman müşterinin GÜNCEL zaman dilimini kullanacak */}
                <td>{formatDate(order.created_at)}</td>
                <td><Link to={`/account/orders/${order.id}`} className="details-btn">{t('view_order_details_btn')}</Link></td>
			</tr>
        );
    });
  };

  return (
    <div className="page-container account-page">
      <h1>{t('my_orders')}</h1>
      <div className="account-content-card">
        <div className="status-tabs">
            <button onClick={() => setActiveTab('total')} className={activeTab === 'total' ? 'active' : ''}>{t('total')} ({orders.length})</button>
            <button onClick={() => setActiveTab('pending')} className={activeTab === 'pending' ? 'active' : ''}>{t('pending')} ({pendingOrders.length})</button>
            <button onClick={() => setActiveTab('completed')} className={activeTab === 'completed' ? 'active' : ''}>{t('completed')} ({completedOrders.length})</button>
        </div>
        <table className="jobs-table customer-orders-table">
          <thead>
            <tr className="table-header">
              <th>{t('order_no')}</th>
              <th>{t('order_page_lang')}</th>
              <th>{t('status')}</th>
              <th>{t('amount')}</th>
			        <th>{t('order_page_date')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="table-body">
            {renderContent()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomerOrdersPage;